import React from "react";
import FAQ from "../../pages/Faq";

import { Helmet } from "react-helmet-async";

import {
  FiClock,
  FiFileText,
  FiAlertTriangle,
  FiUsers,
  FiUserCheck,
  FiArchive,
  FiCheckSquare,
  FiBarChart2,
  FiCheckCircle,
} from "react-icons/fi";
import { Check } from "lucide-react";
import SalaryRevisionLetterGeneratorForm from "./SalaryRevisionLetterGeneratorForm";

import {
  FaUser,
  FaClipboardList,
  FaCalendar,
  FaWallet,
  FaUserCheck,
  FaChartBar,
  FaFileContract,
  FaFileLines,
  FaEnvelopeOpenText,
  FaEnvelope,
  FaCaretRight,
} from "react-icons/fa6";

const salaryrevisionLetterContent = [
  {
    icon: FaUser,
    title: "Employee Details",
    description:
      "The employee's name, ID, designation, department and work location.",
  },
  {
    icon: FaClipboardList,
    title: "Revision Confirmation",
    description:
      "A formal statement confirming that the employee's compensation has been revised.",
  },
  {
    icon: FaCalendar,
    title: "Effective Date",
    description: "The date from which the updated salary structure will apply.",
  },
  {
    icon: FaWallet,
    title: "Previous Compensation",
    description:
      "The employee's current CTC, gross salary, fixed salary and variable pay.",
  },
  {
    icon: FaUserCheck,
    title: "Revised Compensation",
    description:
      "The updated CTC, salary components, allowances, incentives and employer contributions.",
  },
  {
    icon: FaChartBar,
    title: "Revision Amount or Percentage",
    description: "The total increase or adjustment in compensation.",
  },
  {
    icon: FaFileContract,
    title: "Reason for Revision",
    description:
      "An optional explanation such as performance, promotion, market correction or restructuring.",
  },
  {
    icon: FaFileLines,
    title: "Salary Annexure",
    description: "A detailed breakdown of the revised compensation structure.",
  },
];
const steps = [
  {
    number: 1,
    title: "Enter Company Details",
    description:
      "Add the company name, address and authorised signatory information.",
  },
  {
    number: 2,
    title: "Add Employee Information",
    description: "Enter the employee's name, ID, designation and department.",
  },
  {
    number: 3,
    title: "Select the Revision Type",
    description:
      "Choose performance revision, promotion revision, market correction, retention or compensation restructuring.",
  },
  {
    number: 4,
    title: "Enter Current Salary",
    description: "Add the employee's existing CTC and salary components.",
  },
  {
    number: 5,
    title: "Add Revised Compensation",
    description:
      "Enter the revised salary, allowances, benefits and revision percentage.",
  },
  {
    number: 6,
    title: "Select the Effective Date",
    description: "Choose the date from which the Salary Revision will apply.",
  },
  {
    number: 7,
    title: "Add Arrears or Role Changes",
    description:
      "Mention any retrospective payment, designation change or department change.",
  },
  {
    number: 8,
    title: "Generate and Review",
    description:
      "Generate the Revised Salary Letter and verify all compensation figures before issuing it.",
  },
];
const features = [
  <>
    <strong>HR Professionals:</strong> Prepare salary revision communication for
    employees across grades, departments and locations.
  </>,
  <>
    <strong>Business Owners:</strong> Formally communicate revised compensation
    without requiring a large HR department.
  </>,
  <>
    <strong>Professional Firms:</strong> Issue Salary Revision Letters to
    associates, consultants, managers and leadership employees.
  </>,
  <>
    <strong>Startups and SMEs:</strong> Generate consistent compensation
    documents without maintaining multiple manual templates.
  </>,
  <>
    <strong>Corporate Organisations:</strong> Document appraisals, market
    corrections, promotions and salary restructuring.
  </>,
  <>
    <strong>Educational Institutions:</strong> Prepare Revised Salary Letters
    for teachers, administrators and support employees.
  </>,
];
const Faqs1 = [
  {
    title: "How Do I Write a Salary Revision Letter?",
    content:
      "Start with the employee's details, revised compensation, effective date, and applicable salary components. A salary revision letter should clearly communicate what has changed and when the new compensation takes effect.",
  },
  {
    title: "What Should I Mention in a Salary Revision Letter?",
    content:
      "Include the employee's name, designation, revised CTC or salary, changed allowances or components, effective date, and other relevant compensation details required by your HR process.",
  },
  {
    title:
      "What Is the Difference Between Salary Revision and Salary Increment?",
    content:
      "A salary increment generally refers to an increase in pay. Salary revision can be broader and may involve changes to CTC, allowances, fixed pay, variable pay, or the overall salary structure.",
  },
  {
    title: "Can I Use a Salary Revision Letter for CTC Changes?",
    content:
      "Yes. A salary revision letter is useful when an employee's CTC or salary structure changes and the revised compensation needs to be formally communicated.",
  },
  {
    title: "When Should a Salary Revision Letter Be Issued?",
    content:
      "It can be issued after an appraisal, promotion, role change, market adjustment, or any approved change to an employee's compensation structure.",
  },
  {
    title: "Should a Salary Revision Letter Show the Old and New Salary?",
    content:
      "Including both figures can make the salary change easier to understand. HR teams may also include a detailed salary breakup when multiple compensation components are revised.",
  },
  {
    title: "How Can I Create a Salary Revision Letter Online?",
    content:
      "Use a Salary Revision Letter Generator to prepare the document without creating the format from scratch. Add the required compensation details, generate the letter, and download the finished document.",
  },
];

const salaryrevisionLetterPoints = [
  "Annual CTC Revision",
  "Fixed Salary Changes",
  "Variable Pay Updates",
  "Allowances and Contributions",
  "Promotion-Based Salary Revision",
  "Market Correction and Restructuring",
];

const companystepsFeatures = [
  {
    id: 1,
    icon: <FiClock />,
    title: "Save HR Time",
    desc: "Generate a relieving letter without drafting every exit document manually",
  },
  {
    id: 2,
    icon: <FiFileText />,
    title: "Maintain a Standard Format",
    desc: "Use a consistent relieving letter format for employees across departments and locations",
  },
  {
    id: 3,
    icon: <FiCheckCircle />,
    title: "Reduce Compensation Errors",
    desc: "Structured fields help prevent incorrect salary figures, percentages and effective dates",
  },
  {
    id: 4,
    icon: <FiBarChart2 />,
    title: "Improve Salary Transparency",
    desc: "Clearly show the employee's previous and revised compensation",
  },
  {
    id: 5,
    icon: <FiCheckSquare />,
    title: "Support Payroll Accuracy",
    desc: "Document approved Salary Revision details before updating employee payroll",
  },
  {
    id: 6,
    icon: <FiArchive />,
    title: "Maintain Employee Records",
    desc: "Store a clear record of compensation changes in the employee's HR file",
  },
];
const allsalaryrevisionpoints = [
  "Salary increase",
  "Fixed and variable pay",
  "Revised allowances",
  "Employer contributions",
  "Promotion compensation",
  "Salary restructuring without",
  "changing total CTC",
];

const salaryRevisionPoints = [
  "Annual appraisal",
  "Performance",
  "Retention",
  "Market correction",
  "Additional responsibilities",
];
const schema = [
  // =========================================================
  // SCHEMA 1: WebApplication
  // =========================================================
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Waggex Free Salary Revision Letter Generator",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description:
      "Free online salary revision letter generator. Enter company details, employee information, current compensation, revised salary components, and effective date to create a professional salary revision letter PDF instantly.",
    url: "https://www.waggex.com/hr-documents/salaryrevision-letter-generator",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
    },
    featureList: [
      "Free salary revision letter generator India",
      "Online CTC revision letter format India",
      "Salary revision letter PDF download",
      "Compensation restructure letter template India",
      "Professional pay revision letter maker India",
    ],
  },

  // =========================================================
  // SCHEMA 2: FAQPage
  // =========================================================
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is salary revision?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Salary Revision is an approved change in an employee's salary, compensation structure, allowances, benefits, or employer contributions issued after an appraisal, promotion, market correction, or restructuring.",
        },
      },
      {
        "@type": "Question",
        name: "Is salary revision always an increment?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "No. Salary revision can include any approved compensation change including restructuring existing components or changing allowances even when the total CTC remains the same.",
        },
      },
      {
        "@type": "Question",
        name: "Should a salary annexure be included?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes, where the revised compensation involves multiple components. A salary annexure breaks down fixed pay, allowances, variable components, and employer contributions for complete payroll reference.",
        },
      },
      {
        "@type": "Question",
        name: "Can salary revision be linked to promotion?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. A salary revision frequently accompanies a promotion when the new role comes with a revised compensation structure. Waggex allows the revision type to be specified so promotion-linked revisions are clearly documented.",
        },
      },
    ],
  },

  // =========================================================
  // SCHEMA 3: Organization
  // =========================================================
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Waggex",
    url: "https://www.waggex.com/",
    logo: "https://www.waggex.com/waggex-payroll-software-logo.png",
    description:
      "Waggex provides free HR document generation tools and HRMS software for Indian businesses.",
    areaServed: "IN",
  },
];
export default function SalaryIncrementLetterGenerator() {
  return (
    <>
      <Helmet>
        <title>Make Free Salary Revision Letter Online & Download PDF</title>
        <meta
          name="description"
          content="Create and download salary revision letter in minutes. Enter employee details, update pay components, set the effective date, and download your PDF"
        />
        <meta
          name="keywords"
          content="salary revision letter generator online,salary revision letter format India,free salary revision letter maker India,CTC revision letter template India,compensation revision letter PDF India,employee salary restructure letter India,salary revision letter Word format India,pay revision letter format India,revised salary letter India"
        />
        <link
          rel="canonical"
          href="https://www.waggex.com/hr-documents/salaryrevision-letter-generator"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema),
          }}
        />
      </Helmet>
      <div className="px-2 py-3 sm:px-4">
        <section className="rounded-xl border border-white px-2 sm:px-5 py-7 shadow-sm backdrop-blur bg-[#ffffff40]">
          <div className="text-center mb-10">
            <h1 className="text-[#10324F] text-lg sm:text-3xl font-roboto font-bold leading-snug mb-4">
              Free Salary Revision Letter Generator Online
            </h1>

            <p className="mx-auto mt-8 max-w-5xl text-sm sm:text-lg leading-6 text-[#10324F] min-h-18 sm:min-h-12">
              Create a professional salary revision letter with updated CTC,
              salary structure, allowances, and effective date. Generate a clear
              document ready to share
            </p>

            <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row mb-10">
              <button
                onClick={() =>
                  (window.location.href = "https://app.waggex.com/signup")
                }
                className="w-full sm:w-auto bg-[#10324F] hover:bg-[#10324F] transition-all duration-300 text-white px-14 py-3 rounded-xl shadow-md text-sm sm:text-base font-medium"
              >
                Start Free Trial
              </button>

              <button
                onClick={() => (window.location.href = "/contact-us")}
                className="w-full sm:w-auto border border-[#10324F] text-[#10324F] hover:bg-[#10324F] hover:text-white transition-all duration-300 px-14 py-3 rounded-xl text-sm sm:text-base font-medium"
              >
                Book a Demo
              </button>
            </div>
          </div>
          <SalaryRevisionLetterGeneratorForm />
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 ">
                {/* Left Content */}
                <div>
                  <div className="mt-4">
                    <div className="mb-6 flex items-center gap-3">
                      <span className="w-15.5 h-0.5 rounded bg-linear-to-r from-[#10324F] via-[#607D8B] to-transparent shadow-[0_0_6px_rgba(16,50,79,0.15)]"></span>
                      <span className="text-sm font-medium uppercase tracking-wider text-[#10324F]">
                        WHAT IS SALARY REVISION?
                      </span>
                    </div>

                    <h2 className="text-xl md:text-3xl font-semibold text-[#10324F] mt-3 mb-5">
                      Free Salary Revision Letter Generator Online
                    </h2>

                    <p className="text-[#10324F] mb-6 leading-relaxed max-w-2xl">
                      Make a professional salary revision letter instantly to
                      communicate changes in an employee’s CTC, salary
                      structure, allowances, or other compensation details
                    </p>
                  </div>
                </div>

                {/* Right Content */}
                <div className="flex flex-col">
                  {salaryrevisionLetterPoints.map((item, index) => (
                    <React.Fragment key={item}>
                      <div className="relative z-10 flex min-h-12.5 items-center justify-center rounded-lg border border-white  bg-linear-to-b from-white to-[#D1DFEA]/80  hover:bg-white/90 px-4 py-3 text-center text-sm font-medium text-[#10324F] shadow-sm backdrop-blur-sm transition sm:min-h-12.5 sm:text-base">
                        {item}
                      </div>

                      {index < salaryrevisionLetterPoints.length - 1 && (
                        <div className="mx-auto h-6 border-l border-dashed border-[#10324F]/80" />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center max-w-5xl mx-auto">
                <p className="text-sm font-medium uppercase text-[#10324F] sm:text-base">
                  WHAT DOES A SALARY REVISION LETTER INCLUDE?
                </p>
                <h2 className="mt-4 text-xl leading-7.5 sm:text-3xl font-roboto font-bold text-[#16324F] uppercase">
                  Create a Salary Revision Letter That’s Ready to Issue
                </h2>

                <p className="mt-6 text-sm leading-6 sm:text-base text-[#10324F] px-2 font-roboto  text-center max-w-3xl mx-auto">
                  Turn updated employee compensation details into a professional
                  Salary Revision Letter. Clearly document revised CTC,
                  allowances, salary components, and the effective date
                </p>
              </div>

              {/* Cards */}
              <div className="mt-8 sm:mt-12 grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
                {salaryrevisionLetterContent.map((item, index) => {
                  const Icon = item.icon;

                  return (
                    <div
                      key={index}
                      className="group flex min-h-25.5 items-start gap-4 rounded-xl border border-white/90 bg-linear-to-b from-white/60 to-[#D1DFEA]/70  hover:bg-white/90 px-5 py-5 transition-all duration-200 shadow-sm"
                    >
                      {/* Icon */}
                      <div className="flex shrink-0 items-center justify-center pt-1">
                        <Icon className="text-2xl text-[#10324F]" />
                      </div>

                      {/* Content */}
                      <div>
                        <h3 className="text-sm sm:text-lg font-semibold leading-5 text-[#10324F]">
                          {item.title}
                        </h3>

                        <p className="mt-2 text-xs sm:text-sm leading-5 text-[#10324F]">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center">
                <p className="text-sm font-medium uppercase text-[#10324F] sm:text-base">
                  HOW TO CREATE A RELIEVING LETTER ONLINE
                </p>

                <h2 className="mt-4 text-xl font-bold leading-tight text-[#10324F] sm:text-3xl">
                  Create Your Relieving Letter in 3 Simple Steps
                </h2>
              </div>

              {/* Steps */}

              {/* ROW 1 */}
              <div className=" mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                {steps.slice(0, 4).map((step, index) => (
                  <div key={step.number} className="relative">
                    {/* LEFT LINE */}
                    <div className="absolute left-0 right-1/2 top-6.5  border-t border-dashed border-[#10324F] hidden lg:block" />

                    {/* RIGHT LINE + ARROW */}
                    {index < 3 && (
                      <div className="absolute left-1/2 right-0 top-6.5  border-t border-dashed border-[#10324F] hidden lg:block">
                        <span className="absolute -right-0.5 -top-3.75 text-lg text-[#10324F]">
                          ▶
                        </span>
                      </div>
                    )}

                    {/* STEP BUTTON */}
                    <div className="relative z-10 mx-auto flex h-13 w-32.5 items-center justify-center rounded-xl bg-[#10324F] text-lg font-medium text-white">
                      Step {step.number}
                    </div>

                    {/* CONTENT */}
                    <div className="mx-auto mt-5 max-w-70 px-2 text-center mb-8 sm:mb-4">
                      <h3 className="text-lg font-bold leading-6 text-[#10324F]">
                        {step.title}
                      </h3>

                      <p className="mt-2 text-sm leading-5 text-[#1324F]">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* ROW 2 */}
              <div className=" mt-4 lg:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                {steps.slice(4, 8).map((step, index) => (
                  <div key={step.number} className="relative">
                    {/* LEFT LINE — IMPORTANT FOR STEP 5 */}
                    <div className="absolute left-0 right-1/2 top-6.5 hidden border-t border-dashed border-[#10324F] lg:block" />

                    {/* RIGHT LINE + ARROW */}
                    {index < 3 && (
                      <div className="absolute left-1/2 right-0 top-6.5 hidden border-t border-dashed border-[#10324F] lg:block">
                        <span className="absolute -right-0.5 -top-3.75 text-lg text-[#10324F]">
                          ▶
                        </span>
                      </div>
                    )}

                    {/* STEP BUTTON */}
                    <div className="relative z-10 mx-auto flex h-13 w-32.5 items-center justify-center rounded-xl bg-[#163957] text-lg font-medium text-white">
                      Step {step.number}
                    </div>

                    {/* CONTENT */}
                    <div className="mx-auto mt-5 max-w-70 px-2 text-center mb-8 sm:mb-4">
                      <h3 className="text-lg font-bold leading-6 text-[#10324F]">
                        {step.title}
                      </h3>

                      <p className="mt-2 text-sm leading-5 text-[#10324F]">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-8 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-2 lg:gap-8">
                {/* LEFT CONTENT */}
                <div>
                  {/* Small heading */}

                  <div className="mb-6 flex items-center gap-3">
                    <span className="w-15.5 h-0.5 rounded bg-linear-to-r from-[#10324F] via-[#607D8B] to-transparent shadow-[0_0_6px_rgba(16,50,79,0.15)]"></span>
                    <span className="text-sm font-medium uppercase tracking-wider text-[#10324F]">
                      UNDERSTANDING THE DIFFERENCE
                    </span>
                  </div>

                  {/* Main heading */}
                  <h2 className="text-xl font-semibold leading-tight text-[#10324F] sm:text-3xl">
                    Salary Revision Letter vs Salary Increment Letter
                  </h2>

                  {/* Intro */}
                  <p className="mt-5 max-w-2xl text-sm leading-8 text-[#10324F]">
                    Choosing the right letter makes salary changes easier to
                    communicate and document. Use an increment letter for a
                    salary increase, or a revision letter when multiple
                    compensation details are updated
                  </p>
                  <div className="py-4 max-w-xl">
                    <div className="flex flex-col gap-2">
                      {/* Salary Increment Letter */}
                      <div>
                        <h2 className="text-sm font-semibold text-[#10324F] mb-2">
                          When to Use a Salary Increment Letter
                        </h2>
                        <ul className="list-none p-0 m-0 space-y-2">
                          <li className="text-sm leading-relaxed text-[#10324F] pl-6 relative before:content-['•'] before:absolute before:left-0 before:text-[#10324F] before:font-bold before:text-lg">
                            Use it when the employee's salary is being
                            increased, such as after an appraisal or performance
                            review.
                          </li>
                          <li className="text-sm leading-relaxed text-[#10324F] pl-6 relative before:content-['•'] before:absolute before:left-0 before:text-[#10324F] before:font-bold before:text-lg">
                            Best suited for communicating the increment amount,
                            percentage, revised salary, and effective date.
                          </li>
                        </ul>
                      </div>

                      {/* Salary Revision Letter */}
                      <div>
                        <h2 className="text-sm font-semibold text-[#10324F] mb-2">
                          When to Use a Salary Revision Letter
                        </h2>
                        <ul className="list-none p-0 m-0 space-y-2">
                          <li className="text-sm leading-relaxed text-[#10324F] pl-6 relative before:content-['•'] before:absolute before:left-0 before:text-[#10324F] before:font-bold before:text-lg">
                            Use it when multiple compensation components are
                            changing, such as CTC, allowances, fixed pay, or
                            variable pay.
                          </li>
                          <li className="text-sm leading-relaxed text-[#10324F] pl-6 relative before:content-['•'] before:absolute before:left-0 before:text-[#10324F] before:font-bold before:text-lg">
                            Useful when the overall salary structure is being
                            revised, rather than only increasing the salary.
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* RIGHT COMPARISON */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {/* ================= OFFER LETTER ================= */}
                  <div className="flex flex-col gap-3">
                    {/* HEADER CARD */}
                    <div className="flex min-h-20.5 items-center gap-5 rounded-xl border border-white bg-linear-to-b from-white/60 to-[#D1DFEA]/30 px-3 py-4 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <FaEnvelopeOpenText className="shrink-0 text-[34px] text-[#10324F]" />

                      <h3 className="text-sm font-semibold text-[#10324F]">
                        Salary Increment Letter
                      </h3>
                    </div>

                    {/* LIST CARD */}
                    <div className="min-h-80 rounded-xl border border-white bg-linear-to-b  from-white/60 to-[#D1DFEA]/30 px-2 py-4 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <div className="space-y-5">
                        {salaryRevisionPoints.map((point) => (
                          <div key={point} className="flex items-start gap-4">
                            <FaCaretRight className="mt-1 shrink-0 text-sm text-[#10324F]" />

                            <span className="text-sm leading-6 text-[#10324F]">
                              {point}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* ================= APPOINTMENT LETTER ================= */}
                  <div className="flex flex-col gap-3">
                    {/* HEADER CARD */}
                    <div className="flex min-h-20.5 items-center gap-5 rounded-xl border border-white bg-linear-to-b from-white/60 to-[#D1DFEA]/30 px-3 py-4 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <FaEnvelope className="shrink-0 text-[34px] text-[#10324F]" />

                      <h3 className="text-sm font-semibold text-[#10324F]">
                        Salary Revision Letter
                      </h3>
                    </div>

                    {/* LIST CARD */}
                    <div className="min-h-80 rounded-xl border border-white bg-linear-to-b from-white/60 to-[#D1DFEA]/30 px-2 py-4 shadow-sm transition-all duration-200 hover:bg-white/30">
                      <div className="space-y-5">
                        {allsalaryrevisionpoints.map((point) => (
                          <div key={point} className="flex items-start gap-4">
                            <FaCaretRight className="mt-1 shrink-0 text-sm text-[#10324F]" />

                            <span className="text-sm leading-6 text-[#10324F]">
                              {point}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center max-w-5xl mx-auto">
                <p className="text-sm font-normal text-[#10324F] uppercase mb-4">
                  WHY USE AN ONLINE SALARY REVISION LETTER FORMAT?
                </p>
                <h2 className="text-xl leading-7.5 sm:text-3xl font-roboto font-bold text-[#16324F]  uppercase">
                  Create Salary Revision Letter Without Login
                </h2>
              </div>
              <div className="mt-8 sm:mt-12 grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-3 gap-4">
                {companystepsFeatures.map((item) => {
                  // const Icon = item.icon;

                  return (
                    <div
                      key={item.id}
                      className="rounded-xl border border-white/90  bg-linear-to-b from-white/60 to-[#D1DFEA]/60  hover:bg-white/30 p-4 duration-200 shadow-sm transition-all"
                    >
                      {/* Top Row */}
                      <div className="flex items-center justify-center mb-5">
                        {/* <div className="w-14 h-10 rounded-md bg-[#16324F] font-roboto text-white flex items-center justify-center text-lg font-bold shadow">
                          {item.id}
                        </div> */}
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#10324F] text-xl text-white">
                          {item.icon}
                        </div>
                      </div>

                      {/* Content */}
                      <h2 className="text-center text-sm sm:text-base font-semibold text-[#10324F] font-roboto">
                        {item.title}
                      </h2>

                      <p className="mt-2 text-center text-sm leading-6 text-[#10324F] font-roboto">
                        {item.desc}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="w-full px-4 py-8 sm:px-6 md:px-8 lg:px-10 mt-8 rounded-xl border border-white  shadow-sm backdrop-blur-2xl">
            <div className="mt-10 mb-10">
              {/* Heading */}
              <div className="text-center max-w-5xl mx-auto mb-4">
                <p className="text-sm font-normal text-[#10324F] uppercase mb-4">
                  WHO CAN USE THIS SALARY REVISION FORMAT?
                </p>
                <h2 className="font-roboto text-center text-[#10324F] text-xl md:text-3xl font-bold uppercase tracking-wide">
                  A Simple Solution for Every Organisation
                </h2>
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6 place-items-start">
                {features.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 text-[#10324F] px-2 py-3  rounded-2xl"
                  >
                    {/* Check Icon */}
                    <div className="mt-1 flex items-center justify-center text-[#10324F]">
                      <Check size={30} aria-hidden="true" />
                    </div>

                    {/* Text */}
                    <p className="font-roboto text-sm  font-medium leading-relaxed">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
          <FAQ
            title="Frequently Asked Question"
            items={Faqs1}
            defaultOpenIndex={0}
          />
          <section className="w-full bg-[#F4F6F8]/60 px-6 py-10 rounded-xl border border-white mt-8">
            <div className="max-w-6xl mx-auto text-center mt-4 mb-4">
              <h2 className="text-[#10324F] text-xl sm:text-3xl font-roboto font-bold uppercase">
                Create Your Salary Revision For Free
              </h2>

              <p className="mt-4 text-[#10324F] text-sm sm:text-base">
                Prepare a professional salary revision letter with updated CTC,
                salary components, allowances, and effective date. Create it
                free, no login required, and download the PDF instantly
              </p>

              <div className="mt-8 flex flex-col sm:flex-row justify-center items-center gap-4">
                <button
                  className="
              w-full sm:w-auto
              min-w-55
              px-8 py-3
              rounded-xl
              bg-[#10324F]
              text-white text-xs sm:text-sm
              font-medium
              shadow-md
              transition-all duration-300
              hover:scale-105
            "
                  onClick={() => {
                    window.scrollTo({
                      top: 0,
                      behavior: "smooth",
                    });
                  }}
                >
                  Generate Salary Revision Letter
                </button>

                <button
                  className="
              w-full sm:w-auto
              min-w-55
              px-8 py-3 text-xs sm:text-sm
              rounded-xl
              border border-[#10324F]
              text-[#10324F]
              font-medium
              transition-all duration-300
              hover:bg-white
              hover:text-[#16385C]
            "
                  onClick={() => (window.location.href = "/all-hr-documents")}
                >
                  Explore more
                </button>
              </div>
            </div>
          </section>
        </section>
      </div>
    </>
  );
}
